import React from "react";
import "./SkedCard.css";
import {Accordion, ListGroup} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClock, faCode} from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import {tagColor, getBondColor} from "../../../../../../lib/ui/colorUtils";
import ReactJson from "react-json-view";
import { useTimezone } from "../../../../contexts/TimezoneContext";

function SkedCard({ schedules = [] }) {
    const { formatDate, getRelativeTime } = useTimezone();

    function renderDaysOfWeek(daysArray, scheduleEnabled = true) {
        if (!daysArray || !Array.isArray(daysArray)) return null;

        // Check if all 7 days are enabled
        const allDaysEnabled = daysArray.length === 7 && daysArray.every(day => day === true);

        // If all days are enabled, show a single "Daily" badge
        if (allDaysEnabled) {
            return (
                <div
                    style={{
                        width: '172px', // 7 badges (22px each) + 6 gaps (3px each) = 154px + 18px = 172px
                        height: '22px',
                        borderRadius: '11px',
                        backgroundColor: scheduleEnabled ? '#5b8fd9' : '#6c757d',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 'bold',
                    }}
                >
                    Daily
                </div>
            );
        }

        // Otherwise, show individual day badges
        const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

        return (
            <div className="d-inline-flex" style={{gap: '3px'}}>
                {daysArray.map((dayEnabled, idx) => (
                    <div
                        key={idx}
                        style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            backgroundColor: (scheduleEnabled && dayEnabled) ? '#5b8fd9' : '#6c757d',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            opacity: dayEnabled ? 1 : 0.4,
                        }}
                    >
                        {dayLabels[idx]}
                    </div>
                ))}
            </div>
        );
    }

    function getSortableTime(schedule) {
        // Return a numeric value in seconds from midnight for sorting
        if (!schedule.mark || schedule.seconds === undefined) return 999999;

        if (schedule.mark === 'midnight') {
            return schedule.seconds;
        }

        // Approximate times for astronomical events (in seconds from midnight)
        const astronomicalTimes = {
            'dawn': 18000,      // ~5:00 AM
            'sunrise': 21600,   // ~6:00 AM
            'sunset': 64800,    // ~6:00 PM
            'dusk': 68400       // ~7:00 PM
        };

        const baseTime = astronomicalTimes[schedule.mark];
        if (baseTime !== undefined) {
            return baseTime + schedule.seconds;
        }

        return 999999; // Unknown mark types go to end
    }

    function renderGroupProperties(group) {
        const infoKeys = ["state", "properties", "actions"];
        return (<Accordion className={"mt-2"}>
            {infoKeys.map((key, index) => group[key] ? (<Accordion.Item eventKey={index.toString()} key={key}>
                <Accordion.Header>{key.charAt(0).toUpperCase() + key.slice(1)}</Accordion.Header>
                <Accordion.Body className={"p-3"}>
                    {typeof group[key] === "object" && !Array.isArray(group[key]) ? (Object.entries(group[key]).sort(([a], [b]) => {
                        if (a.startsWith("_") && !b.startsWith("_")) return 1;
                        if (!a.startsWith("_") && b.startsWith("_")) return -1;
                        return a.localeCompare(b);
                    }).map(([subKey, subValue]) => (
                        <FloatingLabel label={subKey.charAt(0).toUpperCase() + subKey.slice(1)}
                                       key={subKey}>
                            <Form.Control readOnly={true} value={subValue ?? ""}/>
                        </FloatingLabel>))) : (<FloatingLabel label={key.charAt(0).toUpperCase() + key.slice(1)}>
                        <Form.Control readOnly={true} value={group[key] ?? ""}/>
                    </FloatingLabel>)}
                </Accordion.Body>
            </Accordion.Item>) : null)}
        </Accordion>);
    }


    if (!schedules || schedules.length === 0) {
        return null;
    }

    // Sort schedules by time
    const sortedSchedules = [...schedules].sort((a, b) => {
        return getSortableTime(a) - getSortableTime(b);
    });

    return (<Accordion defaultActiveKey="0" className="mb-2">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <div>
                                <FontAwesomeIcon icon={faClock} className="me-2" />
                                Schedules
                            </div>
                            <Badge bg="dark">
                                {schedules.length}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="p-2">
                        <Accordion alwaysOpen>
                            {sortedSchedules.map((schedule, index) => {
                                const scheduleId = schedule.id || schedule.schedule_id || index;
                                return <Accordion.Item eventKey={`${scheduleId}-sked`}
                                                       key={`${scheduleId}-sked`}>
                                    <Accordion.Header>
                                        <div className="d-flex flex-wrap align-items-center w-100" style={{gap: '0.5rem'}}>
                                            {/* Left-aligned items */}
                                            <Badge bg={schedule.enabled ? "primary" : "secondary"} className="flex-shrink-0">
                                                <FontAwesomeIcon icon={faClock} className={"me-1"}/> {schedule.calculated_time || "Schedule"}
                                            </Badge>
                                            <span className="flex-grow-1 text-truncate" style={{minWidth: 0}}>
                                                {schedule.sources && schedule.sources.length > 1 ? (
                                                    <span className="fw-bold">Multiple Sources ({schedule.sources.length})</span>
                                                ) : schedule.source_name ? (
                                                    <span className="fw-bold">{schedule.source_name}</span>
                                                ) : null}
                                                {schedule.source_name && schedule.action && <span> - </span>}
                                                {schedule.action && (
                                                    <span>{schedule.action}</span>
                                                )}
                                                {!schedule.source_name && !schedule.action && (
                                                    <span>{schedule.name || `Schedule ${index + 1}`}</span>
                                                )}
                                            </span>

                                            {/* Right-aligned items - Bond ID before Source Type */}
                                            <div className="d-flex flex-shrink-0 ms-auto" style={{gap: '0.25rem'}}>
                                                {schedule.bond_ids ? (
                                                    schedule.bond_ids.map((bondId, bondIdx) => (
                                                        <Badge key={bondIdx} className="bondBadge ms-2" style={{'--bond-badge-bg': getBondColor(bondId)}}>{bondId}
                                                        </Badge>
                                                    ))
                                                ) : schedule.bond_id ? (
                                                    <Badge className="bondBadge ms-2" style={{'--bond-badge-bg': getBondColor(schedule.bond_id)}}>{schedule.bond_id}
                                                    </Badge>
                                                ) : null}
                                                {schedule.source_type && (
                                                    <Badge bg="success">
                                                        {schedule.source_type.charAt(0).toUpperCase() + schedule.source_type.slice(1)}
                                                    </Badge>
                                                )}
                                                {schedule.days_of_week && (
                                                    <div className="flex-shrink-0">
                                                        {renderDaysOfWeek(schedule.days_of_week, schedule.enabled)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Accordion.Header>

                                    <Accordion.Body className={"p-3"}>
                                        {schedule.source_type && schedule.source_name && (
                                            <FloatingLabel label={`${schedule.source_type.charAt(0).toUpperCase() + schedule.source_type.slice(1)} Name`} className="mb-2">
                                                <Form.Control readOnly={true} value={schedule.source_name}/>
                                            </FloatingLabel>
                                        )}
                                        {schedule.action && (
                                            <FloatingLabel label="Action" className="mb-2">
                                                <Form.Control readOnly={true} value={schedule.action}/>
                                            </FloatingLabel>
                                        )}
                                        {schedule.argument !== undefined && (
                                            <FloatingLabel label="Argument" className="mb-2">
                                                <Form.Control readOnly={true} value={JSON.stringify(schedule.argument)}/>
                                            </FloatingLabel>
                                        )}
                                        {schedule.name && (
                                            <FloatingLabel label="Name" className="mb-2">
                                                <Form.Control readOnly={true} value={schedule.name}/>
                                            </FloatingLabel>
                                        )}
                                        {schedule.sked_id && (
                                            <FloatingLabel label="Schedule ID" className="mb-2">
                                                <Form.Control readOnly={true} value={schedule.sked_id}/>
                                            </FloatingLabel>
                                        )}
                                        {schedule.calculated_time && (
                                            <FloatingLabel label="Scheduled Time" className="mb-2">
                                                <Form.Control readOnly={true} value={schedule.calculated_time}/>
                                            </FloatingLabel>
                                        )}
                                        {schedule.start_time && (
                                            <FloatingLabel label="Start Time" className="mb-2">
                                                <Form.Control readOnly={true} value={schedule.start_time}/>
                                            </FloatingLabel>
                                        )}
                                        {schedule.mark && (
                                            <FloatingLabel label="Mark" className="mb-2">
                                                <Form.Control readOnly={true} value={schedule.mark}/>
                                            </FloatingLabel>
                                        )}
                                        {schedule.seconds !== undefined && (
                                            <FloatingLabel label="Seconds from mark" className="mb-2">
                                                <Form.Control readOnly={true} value={schedule.seconds}/>
                                            </FloatingLabel>
                                        )}
                                        {schedule.days_of_week && (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small">Days of Week</label>
                                                <div className="d-flex align-items-center" style={{gap: '8px'}}>
                                                    {renderDaysOfWeek(schedule.days_of_week, schedule.enabled)}
                                                </div>
                                            </div>
                                        )}

                                        <div className="d-flex gap-2 mb-2">
                                            {schedule.created && (
                                                <FloatingLabel label={`Created ${getRelativeTime(schedule.created)}`} className="flex-fill">
                                                    <Form.Control readOnly={true} value={formatDate(schedule.created)}/>
                                                </FloatingLabel>
                                            )}
                                            {schedule.modified && (
                                                <FloatingLabel label={`Modified ${getRelativeTime(schedule.modified)}`} className="flex-fill">
                                                    <Form.Control readOnly={true} value={formatDate(schedule.modified)}/>
                                                </FloatingLabel>
                                            )}
                                        </div>

                                        {/* Sources Display - show all sources if multiple */}
                                        {schedule.sources && schedule.sources.length > 1 && (
                                            <div className="mb-3">
                                                <h6>Sources</h6>
                                                <ListGroup>
                                                    {schedule.sources.map((source, idx) => (
                                                        <ListGroup.Item key={idx} className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <Badge bg="success" className="me-2">
                                                                    {source.source_type.charAt(0).toUpperCase() + source.source_type.slice(1)}
                                                                </Badge>
                                                                <span>{source.source_name}</span>
                                                            </div>
                                                            <Badge className="bondBadge ms-2" style={{'--bond-badge-bg': getBondColor(source.bond_id)}}>
                                                                {source.bond_id}
                                                            </Badge>
                                                        </ListGroup.Item>
                                                    ))}
                                                </ListGroup>
                                            </div>
                                        )}

                                        {renderGroupProperties(schedule)}

                                        {/* JSON Viewer Section */}
                                        <div className="mt-3">
                                            <Accordion>
                                                <Accordion.Item eventKey="json">
                                                    <Accordion.Header>
                                                        <FontAwesomeIcon icon={faCode} className="me-2" />
                                                        Raw JSON
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <ReactJson
                                                            src={schedule.raw_schedules || [schedule]}
                                                            theme="ocean"
                                                            collapsed={1}
                                                            displayDataTypes={false}
                                                            displayObjectSize={false}
                                                            enableClipboard={true}
                                                            name={false}
                                                        />
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </div>
                                    </Accordion.Body>

                                </Accordion.Item>
                            })}
                        </Accordion>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>);
}

export default SkedCard;
